import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  acceptAllConsent,
  getSavedConsent,
  hasConsentDecision,
  initGA4,
  rejectAllConsent,
  updateGoogleConsent,
} from "@/lib/cookieConsent"
import { hasUserAuthCookies } from "@/routes/SignIn"

/**
 * Cookie consent for the anonymous page. Rendered as a fixed bar rather than a
 * toast so it can stay compact on a phone — a toast-sized card covered the
 * route stops and the map there. On a phone it hangs from the top edge, over
 * the page title, which is the one strip a route recipient doesn't need; the
 * bottom is taken by the map's route pill and the tab bar. On desktop it keeps
 * the bottom-right card look.
 */
export default function CookieConsentBanner() {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    // Skip GA4 and consent banner for logged-in users (they'll be redirected)
    if (hasUserAuthCookies()) return

    // Load GA4 only on anonymous pages (this component is only rendered there)
    initGA4()

    const saved = getSavedConsent()
    if (saved) {
      updateGoogleConsent(saved)
    } else if (!hasConsentDecision()) {
      setIsOpen(true)
    }
  }, [])

  if (!isOpen) return null

  const reject = () => {
    rejectAllConsent()
    window.analytics?.track("Anonymous_CookieConsentRejected")
    setIsOpen(false)
  }
  const accept = () => {
    acceptAllConsent()
    window.analytics?.track("Anonymous_CookieConsentAccepted")
    setIsOpen(false)
  }

  return (
    <div
      role="dialog"
      aria-label={t("planOrder.cookieConsent.label")}
      className="fixed inset-x-2 top-2 z-[1200] flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-2 text-gray-800 shadow-lg md:inset-x-auto md:bottom-4 md:right-4 md:top-auto md:w-[380px] md:flex-col md:items-stretch md:gap-3 md:p-4"
    >
      <p className="min-w-0 flex-1 text-xs leading-snug md:text-sm">
        {t("planOrder.cookieConsent.notice")}{" "}
        <a
          href="https://slickshift.ai/privacy-policy-slickshift"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-gray-900"
        >
          {t("planOrder.cookieConsent.privacyPolicy")}
        </a>
      </p>
      <div className="flex shrink-0 flex-col gap-1 md:flex-row md:justify-center md:gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs md:h-8 md:px-3 md:text-sm"
          onClick={reject}
        >
          {t("planOrder.cookieConsent.necessaryOnly")}
        </Button>
        <Button
          size="sm"
          className="h-7 bg-blue-600 px-2 text-xs text-white hover:bg-blue-700 md:h-8 md:px-3 md:text-sm"
          onClick={accept}
        >
          {t("planOrder.cookieConsent.acceptAll")}
        </Button>
      </div>
    </div>
  )
}
